<script setup lang="ts">
import PartAppThemeSwitcher from '@parts/part-app-theme-switcher.vue';
import FormSelect from '@form/form-select.vue';
import CoreIcon from '@core/core-icon.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits<{
	'scroll-to-auth': [];
}>();

const { t, locale } = useI18n();

const languages = [
	{ locale: 'ru', name: 'Русский' },
	{ locale: 'en', name: 'English' },
	{ locale: 'es', name: 'Español' },
];

const languagesSelectList = computed(() => {
	return languages.map((lang) => ({
		label: lang.name,
		value: lang.locale,
	}));
});

const selectedLang = ref(locale.value);

watch(selectedLang, (val) => {
	locale.value = val;
	localStorage.setItem('user-locale', val);
	document.querySelector('html')?.setAttribute('lang', val);
});
</script>

<template>
	<header class="section-welcome-header">
		<div class="section-welcome-header__inner">
			<router-link to="/" class="section-welcome-header__logo">
				<img src="/images/logo.svg" alt="Step VPN" class="section-welcome-header__logo-img" />
				<span class="section-welcome-header__logo-text">Step VPN</span>
			</router-link>

			<div class="section-welcome-header__right">
				<div class="section-welcome-header__controls">
					<part-app-theme-switcher class="section-welcome-header__theme" />

					<form-select
						name="lang"
						:icon="{ name: 'website', viewBox: '0 0 25 24' }"
						:list="languagesSelectList"
						v-model="selectedLang"
						class="section-welcome-header__lang"
					/>
				</div>

				<button
					class="section-welcome-header__login-btn"
					@click="emit('scroll-to-auth')"
				>
					<core-icon name="user" viewBox="0 0 19 20" class="section-welcome-header__login-icon" />
					<span class="section-welcome-header__login-text">{{ t('welcome.header.login_button') }}</span>
				</button>
			</div>
		</div>
	</header>
</template>

<style lang="scss">
.section-welcome-header {
	position: sticky;
	top: 0;
	z-index: 100;
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(12px);
	border-bottom: 1px solid rgba(var(--color-active-rgb), 0.08);

	@include theme-dark {
		background: rgba(0, 0, 0, 0.6);
		border-bottom-color: rgba(var(--color-active-rgb), 0.12);
	}

	&__inner {
		max-width: 133.6rem;
		margin: 0 auto;
		padding: 1.2rem 1.5rem;
		display: flex;
		align-items: center;
		justify-content: space-between;

		@include min-desktop {
			padding: 2rem 8rem;
		}
	}

	&__logo {
		display: flex;
		align-items: center;
		gap: 1rem;
		text-decoration: none;
		color: var(--color-purple);

		@include min-desktop {
			gap: 1.6rem;
		}
	}

	&__logo-img {
		width: 3.6rem;
		height: 3.6rem;
		border-radius: 50%;

		@include min-desktop {
			width: 4.8rem;
			height: 4.8rem;
		}
	}

	&__logo-text {
		@include font-16-pro-medium;
		color: var(--color-purple);

		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__right {
		display: flex;
		align-items: center;
		gap: 2rem;
	}

	&__controls {
		display: flex;
		align-items: center;
		gap: 1rem;
		transform: scale(0.75);
		transform-origin: center right;
	}

	&__theme {
		display: none;

		@include min-desktop {
			display: flex;
		}

		&.part-app-theme-switcher {
			background: transparent;

			@include theme-dark {
				border-color: rgba(var(--color-active-rgb), 0.12);
			}
		}

		.part-app-theme-switcher__icons {
			@include theme-dark {
				fill: #fff;
			}
		}

		.form-toggle__switch {
			background: rgba(115, 93, 207, 0.2);

			@include theme-dark {
				background: rgba(115, 93, 207, 0.5);
			}
		}

		.form-toggle__input:checked + .form-toggle__switch {
			@include theme-dark {
				background-color: rgba(115, 93, 207, 0.6);
			}
		}
	}

	&__lang {
		min-width: 12rem;
		flex-shrink: 0;

		.form-select__list {
			background-color: #f0eef5;

			@include theme-dark {
				background-color: #1a1a2e;
			}
		}

		@include min-desktop {
			min-width: 13rem;
		}
	}

	&__login-btn {
		display: flex;
		align-items: center;
		gap: 1.6rem;
		padding: 1.2rem;
		border-radius: 10rem;
		border: 1px solid rgba(var(--color-active-rgb), 0.12);
		background: transparent;
		cursor: pointer;
		transition: background-color ease 0.17s;
		color: var(--color-text);

		@include min-desktop {
			padding: 1.2rem 2.4rem;

			&:hover {
				background: rgba(var(--color-active-rgb), 0.06);
			}
		}
	}

	&__login-icon {
		width: 2rem;
		height: 2rem;
		fill: currentColor;
	}

	&__login-text {
		display: none;
		@include font-16-pro-medium;

		@include min-desktop {
			display: inline;
		}
	}
}
</style>
